import { ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { QuestionnaireStore } from 'src/app/store/questionnaire-store';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthStore } from 'src/app/store/auth-store';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteFilterComponent } from 'src/app/shared/components/autocomplete-filter/autocomplete-filter.component';
import { User } from 'src/app/models/user.model';
import { ApproverCandidate } from 'src/app/models/approverCandidate.model';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { AnswersStore } from 'src/app/store/answers-store';
import { GlobalStore } from 'src/app/store/global-store';
import { LogoDisplayComponent } from "src/app/features/create-questionnaire-page/component/question-board/logo-display/logo-display.component";
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
    selector: 'que-popup-send-approval',
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        ReactiveFormsModule,
        OverlayModule,
        AutoCompleteFilterComponent,
        TranslateDirective, TranslatePipe,
        LogoDisplayComponent,
        SvgIconComponent
    ],
    templateUrl: './popup-send-approval.component.html',
    styleUrls: ['./popup-send-approval.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PopupSendApprovalComponent {
  private dialogRef = inject(MatDialogRef<PopupSendApprovalComponent>);
  #questionnaireStore = inject(QuestionnaireStore);
  #answersStore = inject(AnswersStore);
  #globalStore = inject(GlobalStore);
  private destroyRef = inject(DestroyRef);

  constructor(@Inject(MAT_DIALOG_DATA) public data: { name: string }) {
    this.valueControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ids => {
        const selectedIds = ids ?? [];

        if (selectedIds.length == 2) {
          this.openSelectAdmins = false;
        }
      });
  }
  questionarrieSig$ = toSignal(this.#questionnaireStore.questionnaire$);
  loggedUserDetails$ = toSignal(this.#globalStore.loggedUserDetails$);
  approverAdmins$ = toSignal(this.#answersStore.approverAdmins$);
  isFreeApproverSelection$ = toSignal(this.#globalStore.isFreeApproverSelection$);
  approvers$ = toSignal(this.#globalStore.approvers$);
  amount$ = toSignal(this.#answersStore.amount$);
  valueControl = new FormControl<number[]>([]);

  selectedApproverIds$ = toSignal(
    this.valueControl.valueChanges,
    { initialValue: this.valueControl.value ?? [] }
  );
  approversToDisplay = computed<ApproverCandidate[]>(() => {
    if (!this.isFreeApproverSelection$()) {
      return this.approverAdmins$() ?? [];
    }

    const selectedIds = this.selectedApproverIds$() ?? [];
    const allApprovers = this.approvers$() ?? [];

    return allApprovers
      .filter(user => user.id && selectedIds.includes(user.id))
      .map(user => ({ user }) as ApproverCandidate);
  });

  openSelectAdmins = false;
  positonArea: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    }
  ];

  openAdminsDropdown(): void {
    if (this.isFreeApproverSelection$()) {
      this.openSelectAdmins = true;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    const candidates: { adminId?: number; groupId?: number }[] = this.isFreeApproverSelection$()
      ? (this.valueControl.value ?? []).map(id => ({ adminId: id }))
      : this.approversToDisplay().map(c => c.user?.id ? { adminId: c.user.id } : { groupId: c.groupId });

    this.dialogRef.close({
      slot1: candidates[0],
      slot2: candidates[1],
      requiredAmount: this.amount$()
    });
  }

  validateApprovers(): boolean {
    if (this.isFreeApproverSelection$()) {
      return (this.valueControl.value ?? []).length > 0;
    }

    return (this.approverAdmins$() ?? []).length > 0;
  }

  removeAdmin(item: ApproverCandidate, event: MouseEvent): void {
    event.stopPropagation();

    const id = item.user?.id;
    if (!id) {
      return;
    }

    if (this.isFreeApproverSelection$()) {
      const currentIds = this.valueControl.value ?? [];

      this.valueControl.setValue(
        currentIds.filter(x => x !== id)
      );

      return;
    }
  }
}